import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiEye, FiEyeOff } from 'react-icons/fi';

import { useCompleteTemporaryPasswordChange } from '../../../helpers/api/auth/auth';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { extractPasswordValidationError } from '../../../helpers/passwordValidation';
import {
  translatePasswordError,
  validatePasswordStrengthI18n,
} from '../../../helpers/i18n/passwordValidationI18n';
import { FormField } from '../../ComponnentsUI/common/FormField';
import { Input } from '../../ComponnentsUI/common/Input';
import { Button } from '../../ComponnentsUI/common/Button';
import smallLogo from '../../../small_deepecho_icon.png';
import darksmallLogo from '../../../small_deepecho_icon_dark.png';

/**
 * The last step of signing in with a temporary password, so it is built from the same
 * card, fields and buttons as the login page rather than antd's Form — the two screens
 * appear back to back and any difference between them reads as a different product.
 */
const ForcePasswordChange = () => {
  const { t } = useTranslation('AccountPage');
  const { t: tLogin } = useTranslation('Login');
  const navigate = useNavigate();
  const location = useLocation();

  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    verificationCode?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const { mutate: completePasswordChange, isPending } =
    useCompleteTemporaryPasswordChange();

  // Login decides which of the two flows this is and passes the answer through
  // navigation state. Only an admin-assisted reset of an established account has a
  // code; first login of an account created with a temporary password has none, and
  // asking for one would be an unanswerable prompt.
  const codeRequired = (
    location.state as { verificationCodeRequired?: boolean } | null
  )?.verificationCodeRequired;

  useDocumentTitle(t('forcePassword.documentTitle'));

  // Reloading this page drops the navigation state, leaving no way to tell the flows
  // apart — and guessing strands one of them. Signing in again is cheap (the user
  // still has the password that got them here) and restores the state.
  useEffect(() => {
    if (codeRequired === undefined) {
      navigate('/login', { replace: true });
    }
  }, [codeRequired, navigate]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (codeRequired && !verificationCode.trim()) {
      setFieldErrors({
        verificationCode: t('forcePassword.enterVerificationCode'),
      });
      return;
    }

    const passwordError = validatePasswordStrengthI18n(password);
    if (passwordError) {
      setFieldErrors({ password: passwordError });
      return;
    }

    if (password !== confirmPassword) {
      setFieldErrors({ confirmPassword: t('passwordsDoNotMatch') });
      return;
    }

    setFieldErrors({});

    completePasswordChange(
      {
        ...(codeRequired ? { verification_code: verificationCode.trim() } : {}),
        new_password: password,
      },
      {
        onSuccess: () => {
          navigate('/home');
        },
        onError: (err: { message?: string }) => {
          const passwordValidationError = translatePasswordError(
            extractPasswordValidationError(err)
          );
          if (passwordValidationError) {
            setFieldErrors({ password: passwordValidationError });
            return;
          }

          setError(err.message || t('forcePassword.changePasswordError'));
        },
      }
    );
  };

  // Render nothing while the effect above bounces to login, so the form does not flash
  // in whichever variant the missing state happened to fall back to.
  if (codeRequired === undefined) {
    return null;
  }

  return (
    <div className="min-h-screen w-full flex justify-center items-center p-4 bg-card-color-200 dark:bg-gray-900">
      <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-lg bg-card-color-100 dark:bg-gray-800">
        <div className="flex items-center justify-center mb-6 space-x-3">
          <img
            src={smallLogo}
            className="dark:hidden transition-all duration-300 h-10"
            alt="Deep Echo Logo"
          />
          <img
            src={darksmallLogo}
            className="hidden dark:block transition-all duration-300 h-10"
            alt="Deep Echo Logo"
          />
          <div>
            <h1 className="text-xl font-bold text-title-page-color-100">
              {tLogin('title')}
            </h1>
            <p className="text-xs text-gray-500">{tLogin('subtitle')}</p>
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-title-page-color-100">
            {t('forcePassword.title')}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {codeRequired
              ? t('forcePassword.subtitle')
              : t('forcePassword.subtitleFirstLogin')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {codeRequired && (
            <FormField
              label={t('forcePassword.verificationCode')}
              htmlFor="verificationCode"
              required
              error={fieldErrors.verificationCode}
            >
              <Input
                id="verificationCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value);
                  setFieldErrors((prev) => ({
                    ...prev,
                    verificationCode: undefined,
                  }));
                }}
                className="text-center text-lg tracking-wider bg-gray-50 border-gray-200 focus:bg-white"
              />
            </FormField>
          )}

          <FormField
            label={t('forcePassword.newPassword')}
            htmlFor="password"
            required
            error={fieldErrors.password}
          >
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('forcePassword.newPasswordPlaceholder')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, password: undefined }));
                }}
                className="bg-gray-50 border-gray-200 focus:bg-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </FormField>

          <FormField
            label={t('forcePassword.confirmPassword')}
            htmlFor="confirmPassword"
            required
            error={fieldErrors.confirmPassword}
          >
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('forcePassword.confirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFieldErrors((prev) => ({
                    ...prev,
                    confirmPassword: undefined,
                  }));
                }}
                className="bg-gray-50 border-gray-200 focus:bg-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </FormField>

          <p className="text-xs text-gray-400 dark:text-gray-500">
            {t('forcePassword.requirements')}
          </p>

          {error && (
            <p className="text-red-500 text-sm text-center font-medium">
              {error}
            </p>
          )}

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isPending}
            >
              {isPending
                ? t('forcePassword.saving')
                : t('forcePassword.savePassword')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
